import type { Product } from '@cartazo/contracts'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Icon } from './components/primitives'
import { selectItemCount, useCart } from './features/cart/cartStore'
import { CategoryExplorer } from './features/explorer/CategoryExplorer'
import { useMenu } from './features/menu/useMenu'
import { useNavigator } from './features/navigation/navigatorStore'
import { GestureHint } from './features/onboarding/GestureHint'
import { applyBrand } from './features/theme/theme'
import { track } from './lib/analytics'
import { readContext } from './lib/context'

/**
 * Raíz de la carta.
 *
 * `LazyMotion` con `strict` obliga a usar `m` en lugar de `motion`, que es lo
 * que evita arrastrar el motor de animación completo.
 *
 * PENDIENTE de rendimiento: el chunk de Motion pesa 31 KB gzip y hoy se carga
 * con la primera vista, porque los componentes importan `m` y `AnimatePresence`
 * de forma estática. Pasarlo a carga diferida real exige que el escenario
 * inmersivo sea un chunk aparte, con una primera pantalla sin animaciones. Es
 * la mayor palanca pendiente sobre el presupuesto de bundle.
 */
/**
 * Todo lo que no se ve al abrir la carta se carga cuando hace falta.
 *
 * La primera pantalla es la portada de categorías: no anima, no tiene hojas
 * y no muestra platos. Todo esto —el escenario inmersivo, el carrito, el
 * mapa, la búsqueda— arrastraba Motion al chunk inicial y hacía que la carta
 * pesara 130 KB gzip en vez de los 110 del plan.
 *
 * `fallback={null}`: entre pulsar y que llegue el chunk pasan milisegundos
 * sobre una pantalla que ya tiene contenido. Un spinner ahí parpadea y se
 * lee como un fallo.
 */
const ImmersiveStage = lazy(() =>
  import('./features/immersive/ImmersiveStage').then((m) => ({ default: m.ImmersiveStage })),
)
const CartDrawer = lazy(() =>
  import('./features/cart/CartUI').then((m) => ({ default: m.CartDrawer })),
)
const ModifierSheet = lazy(() =>
  import('./features/cart/CartUI').then((m) => ({ default: m.ModifierSheet })),
)
const MapModal = lazy(() =>
  import('./features/overlays/Overlays').then((m) => ({ default: m.MapModal })),
)
const SearchOverlay = lazy(() =>
  import('./features/overlays/Overlays').then((m) => ({ default: m.SearchOverlay })),
)

export function App() {
  const { slug, tableLabel, productSlug } = readContext()
  const { status, menu, error, stale, reload } = useMenu(slug)

  const phase = useNavigator((s) => s.phase)
  const setPhase = useNavigator((s) => s.setPhase)
  const backToExplorer = useNavigator((s) => s.backToExplorer)
  const attach = useNavigator((s) => s.attach)
  const jump = useNavigator((s) => s.jump)
  const c = useNavigator((s) => s.c)
  const s = useNavigator((s) => s.s)
  const p = useNavigator((s) => s.p)

  const initCart = useCart((state) => state.init)
  const revalidateCart = useCart((state) => state.revalidate)
  const itemCount = useCart(selectItemCount)

  const [productSheet, setProductSheet] = useState<Product | null>(null)

  /**
   * Qué superficies diferidas se han llegado a necesitar.
   *
   * No basta con renderizarlas solo mientras están abiertas: se cerrarían de
   * golpe, sin la animación de salida. Se montan la primera vez que se piden
   * y a partir de ahí se quedan, gobernadas por su prop `open` como siempre.
   */
  const [usadas, setUsadas] = useState<Record<string, boolean>>({})
  const usar = (clave: string) =>
    setUsadas((prev) => (prev[clave] ? prev : { ...prev, [clave]: true }))

  useEffect(() => {
    if (phase === 'map' || phase === 'search' || phase === 'cart') usar(phase)
  }, [phase])

  useEffect(() => {
    if (productSheet) usar('producto')
  }, [productSheet])

  // El carrito se ata al restaurante Y a la mesa: dos mesas del mismo local no
  // pueden compartir pedido.
  useEffect(() => {
    if (slug) void initCart(slug, tableLabel)
  }, [slug, tableLabel, initCart])

  // La navegación necesita el índice plano para conocer sus límites.
  useEffect(() => {
    if (menu) attach(menu)
  }, [menu, attach])

  // Al llegar una carta nueva se contrasta con el carrito guardado: es lo que
  // detecta que un plato se agotó o cambió de precio mientras el comensal
  // decidía.
  useEffect(() => {
    if (menu) revalidateCart(menu)
  }, [menu, revalidateCart])

  useEffect(() => {
    if (menu) applyBrand(menu.raw.restaurant)
  }, [menu])

  /**
   * Salto al plato que pide la URL, UNA sola vez.
   *
   * `saltado` y no una dependencia sobre `menu`: la carta se recarga sola cada
   * pocos minutos y con el SSE, y sin el cerrojo cada refresco devolvería al
   * comensal al plato del enlace aunque llevara diez minutos mirando otra cosa.
   *
   * Si el slug no existe —el plato se borró o se renombró— no se hace nada y la
   * carta abre por la portada. Un enlace viejo no debe acabar en un error.
   */
  const saltado = useRef(false)

  useEffect(() => {
    if (!menu || !productSlug || saltado.current) return

    const destino = menu.flat.find((entry) => entry.product.slug === productSlug)
    if (!destino) return

    saltado.current = true
    jump(destino.c, destino.s, destino.p)
  }, [menu, productSlug, jump])

  /*
   * Analítica: lo que alimenta el embudo del panel.
   *
   * La carta no emitía ningún evento y cuatro de los seis pasos del embudo
   * eran cero por construcción. Ver `lib/analytics.ts`.
   */
  const vistaContada = useRef(false)
  useEffect(() => {
    if (!menu || vistaContada.current) return
    vistaContada.current = true
    track('menu_view')
  }, [menu])

  // Un plato se cuenta como visto cuando está a pantalla completa, que es lo
  // que de verdad es "mirarlo": pasar por la portada de categorías no lo es.
  useEffect(() => {
    if (!menu || phase !== 'immersive') return
    const producto = menu.products[c]?.[s]?.[p]
    if (producto) track('product_view', { entity_type: 'product', entity_id: producto.id })
  }, [menu, phase, c, s, p])

  useEffect(() => {
    if (phase === 'cart') track('cart_open')
  }, [phase])

  if (!slug) {
    return (
      <div className="center-state">
        <h1>Carta no encontrada</h1>
        <p style={{ color: 'var(--t-muted)' }}>
          Escanea el código QR de tu mesa para abrir la carta.
        </p>
      </div>
    )
  }

  if (status === 'loading') {
    return (
      <div className="center-state">
        <div className="spinner" aria-hidden="true" />
        <p role="status">Cargando la carta…</p>
      </div>
    )
  }

  if (status === 'error' || !menu) {
    return (
      <div className="center-state">
        <h1>No pudimos cargar la carta</h1>
        <p style={{ color: 'var(--t-muted)' }}>{error}</p>
        <button type="button" className="button" onClick={reload}>
          Reintentar
        </button>
      </div>
    )
  }

  const restaurant = menu.raw.restaurant
  const currentProduct = menu.products[c]?.[s]?.[p] ?? null

  return (
    <>
      {phase === 'explorer' ? (
        <>
          <header className="explorer__header">
            {/*
              El logo sustituye al nombre cuando existe, no lo acompaña: un logo
              suele contener ya el nombre, y ponerlos juntos lo dice dos veces.
              El `alt` mantiene el nombre para quien no ve la imagen.
            */}
            {restaurant.logo ? (
              <img className="explorer__logo" src={restaurant.logo} alt={restaurant.name} />
            ) : (
              <span className="explorer__brand">{restaurant.name}</span>
            )}
            <button
              type="button"
              className="icon-button"
              onClick={() => setPhase('search')}
              aria-label="Buscar en la carta"
            >
              <Icon name="search" />
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={() => setPhase('map')}
              aria-label="Índice de la carta"
            >
              <Icon name="menu" />
            </button>
          </header>
          <CategoryExplorer menu={menu} />
        </>
      ) : (
        <>
          <Suspense fallback={null}>
            <ImmersiveStage
              menu={menu}
              restaurant={restaurant}
              onOpenProduct={() => setProductSheet(currentProduct)}
            />
          </Suspense>

          <header className="explorer__header">
            <button
              type="button"
              className="icon-button"
              onClick={backToExplorer}
              aria-label="Volver al inicio"
            >
              <Icon name="back" />
            </button>
            <span className="explorer__brand" />
            <button
              type="button"
              className="icon-button"
              onClick={() => setPhase('search')}
              aria-label="Buscar en la carta"
            >
              <Icon name="search" />
            </button>
            <button
              type="button"
              className="icon-button"
              onClick={() => setPhase('map')}
              aria-label="Índice de la carta"
            >
              <Icon name="menu" />
            </button>
            {restaurant.ordering_mode !== 'readonly' && (
              <button
                type="button"
                className="icon-button"
                style={{ position: 'relative' }}
                onClick={() => setPhase('cart')}
                aria-label={`Ver mi pedido, ${itemCount} artículos`}
              >
                <Icon name="cart" />
                {itemCount > 0 && <span className="cart-count">{itemCount}</span>}
              </button>
            )}
          </header>
        </>
      )}

      {/*
        Al ENTRAR al escenario, no al abrir la carta: es cuando los gestos
        empiezan a hacer falta, y así no retrasa el primer vistazo a la comida.
      */}
      <GestureHint active={phase === 'immersive'} />

      {stale && (
        <div className="toast" role="status">
          <span>Sin conexión. Estás viendo la última carta guardada.</span>
        </div>
      )}

      <Suspense fallback={null}>
        {usadas.map && (
          <MapModal menu={menu} open={phase === 'map'} onClose={() => setPhase('immersive')} />
        )}

        {usadas.search && (
          <SearchOverlay
            menu={menu}
            open={phase === 'search'}
            onClose={() => setPhase('immersive')}
          />
        )}
      </Suspense>

      <Suspense fallback={null}>
        {usadas.producto && (
          <ModifierSheet
            // La key remonta la hoja al cambiar de plato, así que su selección
            // nace limpia sin necesidad de un efecto que la reinicie.
            key={productSheet?.id ?? 'none'}
            product={productSheet}
            restaurant={restaurant}
            open={productSheet !== null}
            onClose={() => setProductSheet(null)}
          />
        )}

        {usadas.cart && (
          <CartDrawer
            restaurant={restaurant}
            open={phase === 'cart'}
            onClose={() => setPhase('immersive')}
            onSubmitted={() => setPhase('immersive')}
          />
        )}
      </Suspense>
    </>
  )
}
